import { PaymentButtonEntry, PaymentButtonsState, PaymentButtonsView } from '../../../types/payment-buttons';
import { DraggableAttributes } from '../../components/PaymentButtons';
import { PaymentButtonsArrangerProps } from '../../components/PaymentButtonsArranger';
import { RadioOption } from '../../components/Radio';

const listOf = (state: PaymentButtonsState, asButtons: boolean): PaymentButtonEntry[] =>
    state.entries.filter((entry) => entry.as_button === asButtons);

const toOption = (entry: PaymentButtonEntry): RadioOption => ({ value: entry.value, label: entry.label });

/**
 * Puts the dragged method before another, or at the end of the list when there is none, as a button or in the menu.
 * A button beyond the most the checkout has is refused with a notice.
 */
const drop = (view: PaymentButtonsView, asButton: boolean, before: string | null): void => {
    const { state } = view;
    const value = state.dragged;
    const moving = state.entries.find((entry) => entry.value === value);
    state.dragged = null;
    state.dragOver = null;

    if (!moving || value === before) {
        view.update();

        return;
    }

    if (asButton && !moving.as_button && listOf(state, true).length >= state.maxButtons) {
        state.notice = state.lang.tooMany;
        view.update();

        return;
    }

    const rest = state.entries.filter((entry) => entry.value !== value);
    const at = before === null ? -1 : rest.findIndex((entry) => entry.value === before);
    rest.splice(at === -1 ? rest.length : at, 0, { ...moving, as_button: asButton });

    state.entries = [...rest.filter((entry) => entry.as_button), ...rest.filter((entry) => !entry.as_button)];
    state.notice = '';
    view.update();
    view.arranged(state.entries);
};

/**
 * Where the dragged method would land, as a line on that side of the method the pointer is over: left or right of a
 * button, above or below a menu item.
 */
const marker = (state: PaymentButtonsState, value: string, across: boolean): string | undefined => {
    if (state.dragOver?.value !== value || state.dragged === value) {
        return undefined;
    }

    const { after } = state.dragOver;

    return across
        ? `box-shadow: ${after ? 4 : -4}px 0 0 0 #262626`
        : `box-shadow: inset 0 ${after ? -3 : 3}px 0 0 #262626`;
};

const draggable =
    (view: PaymentButtonsView, asButton: boolean) =>
    (option: RadioOption): DraggableAttributes => {
        const { state } = view;

        return {
            dragged: state.dragged === option.value,
            style: marker(state, option.value, asButton),
            onDragStart: (event: DragEvent): void => {
                state.dragged = option.value;
                state.notice = '';
                event.dataTransfer?.setData('text/plain', option.value);
                if (event.dataTransfer) {
                    event.dataTransfer.effectAllowed = 'move';
                }
                // Drawn faded only once the browser has taken its picture of the method being dragged.
                setTimeout(view.update);
            },
            onDragEnd: (): void => {
                state.dragged = null;
                state.dragOver = null;
                view.update();
            },
            // Over a method: before or after it, by the half of it the pointer is on (across the buttons, down the
            // menu).
            onDragOver: (event: DragEvent): void => {
                event.preventDefault();
                event.stopPropagation();
                if (event.dataTransfer) {
                    event.dataTransfer.dropEffect = 'move';
                }

                const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
                const after = asButton
                    ? event.clientX > box.left + box.width / 2
                    : event.clientY > box.top + box.height / 2;

                if (state.dragOver?.value !== option.value || state.dragOver.after !== after) {
                    state.dragOver = { value: option.value, after };
                    view.update();
                }
            },
            // Onto a method: before it, or before the next one of its list (or at the end) past its half.
            onDrop: (event: DragEvent): void => {
                event.preventDefault();
                event.stopPropagation();

                const list = listOf(state, asButton);
                const at = list.findIndex((entry) => entry.value === option.value);
                const pastHalf = state.dragOver?.value === option.value && state.dragOver.after;

                drop(view, asButton, pastHalf ? (list[at + 1]?.value ?? null) : option.value);
            },
        };
    };

const dropTarget = (view: PaymentButtonsView, asButtons: boolean): PaymentButtonsArrangerProps['toButtons'] => ({
    onDragOver: (event: DragEvent): void => {
        event.preventDefault();
        if (view.state.dragOver !== null) {
            view.state.dragOver = null;
            view.update();
        }
    },
    onDrop: (event: DragEvent): void => {
        event.preventDefault();
        drop(view, asButtons, null);
    },
});

export const getPaymentButtonsArrangerProps = (view: PaymentButtonsView): PaymentButtonsArrangerProps => {
    const { state } = view;

    return {
        buttons: listOf(state, true).map(toOption),
        menu: listOf(state, false).map(toOption),
        draggableButton: draggable(view, true),
        draggableMenuItem: draggable(view, false),
        toButtons: dropTarget(view, true),
        toMenu: dropTarget(view, false),
        notice: state.notice,
        lang: state.lang,
    };
};
